import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { motion } from "framer-motion";
import { useFormContext, useWatch } from "react-hook-form";

const labelStyle = "block text-sm font-medium text-gray-300 mb-2";
const inputStyle =
  "w-full px-4 py-3 rounded-lg border border-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 bg-gray-800 text-gray-200";

function FormStep3({ copy, showErrors }) {
  const {
    register,
    control,
    setValue,
    formState: { errors },
  } = useFormContext();

  const interest = useWatch({
    control,
    name: "interest",
  });

  useEffect(() => {
    if (interest !== "installing" && interest) {
      setValue("capacity", "");
    }
  }, [interest, setValue]);

  const showCapacity = interest === "installing";

  return (
    <div className="space-y-6">
      <div>
        <label htmlFor="interest" className={labelStyle}>
          {copy.fieldLabels.interest}
          <span className="text-red-500 ml-1">*</span>
        </label>
        <motion.select
          id="interest"
          {...register("interest", {
            required: copy.errorMessages.selectOption,
          })}
          className={`${inputStyle} cursor-pointer ${showErrors && errors.interest ? "border-red-500" : ""}`}
          whileFocus={{ scale: 1.01 }}
        >
          <option value="" disabled>
            {copy.interestOptions.selectOption}
          </option>
          <option value="partnership">
            {copy.interestOptions.partnership}
          </option>
          <option value="installing">{copy.interestOptions.installing}</option>
          <option value="other">{copy.interestOptions.other}</option>
        </motion.select>
        {showErrors && errors.interest && (
          <p className="mt-1 text-sm text-red-500">{errors.interest.message}</p>
        )}
      </div>
      {showCapacity && (
        <div>
          <label htmlFor="capacity" className={labelStyle}>
            {copy.fieldLabels.capacity}
          </label>
          <motion.input
            type="text"
            id="capacity"
            {...register("capacity")}
            className={inputStyle}
            placeholder={copy.placeholders.capacity}
            whileFocus={{ scale: 1.01 }}
          />
        </div>
      )}
      <div>
        <label htmlFor="companyName" className={labelStyle}>
          {copy.fieldLabels.companyName}
        </label>
        <motion.input
          type="text"
          id="companyName"
          {...register("companyName")}
          className={inputStyle}
          placeholder={copy.placeholders.companyName}
          whileFocus={{ scale: 1.01 }}
        />
      </div>
      <div>
        <label htmlFor="message" className={labelStyle}>
          {copy.fieldLabels.message}
          <span className="text-red-500 ml-1">*</span>
        </label>
        <motion.textarea
          id="message"
          {...register("message", { required: copy.errorMessages.required })}
          className={`${inputStyle} h-32 ${showErrors && errors.message ? "border-red-500" : ""}`}
          placeholder={copy.placeholders.message}
          whileFocus={{ scale: 1.01 }}
        ></motion.textarea>
        {showErrors && errors.message && (
          <p className="mt-1 text-sm text-red-500">{errors.message.message}</p>
        )}
      </div>
    </div>
  );
}

FormStep3.propTypes = {
  copy: PropTypes.shape({
    fieldLabels: PropTypes.shape({
      interest: PropTypes.string.isRequired,
      capacity: PropTypes.string.isRequired,
      companyName: PropTypes.string.isRequired,
      message: PropTypes.string.isRequired,
    }).isRequired,
    errorMessages: PropTypes.shape({
      selectOption: PropTypes.string.isRequired,
      required: PropTypes.string.isRequired,
    }).isRequired,
    interestOptions: PropTypes.shape({
      selectOption: PropTypes.string.isRequired,
      partnership: PropTypes.string.isRequired,
      installing: PropTypes.string.isRequired,
      other: PropTypes.string.isRequired,
    }).isRequired,
    placeholders: PropTypes.shape({
      capacity: PropTypes.string.isRequired,
      companyName: PropTypes.string.isRequired,
      message: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  showErrors: PropTypes.bool.isRequired,
};

export default React.memo(FormStep3);
